import type { AgentInputItem } from '../types';
import { buildStableHistoryItems } from './refs';
import {
  estimateTokensForItem,
  estimateTokensForItems,
} from './token-estimator';

export type CompactionPlan = {
  compactedRange: { fromItemId: string; toItemId: string };
  preservedItems: AgentInputItem[];
  itemsForSummary: AgentInputItem[];
  insertAfterItemId: string;
  estimatedPreTokens: number;
  estimatedPostTokens: number;
};

export type CompactionPlannerOptions = {
  thresholdTokens?: number;
  force?: boolean;
  preservedTailTokens?: number;
  preservedTailMessages?: number;
  minTextMessagesToKeep?: number;
  estimatedSummaryTokens?: number;
  minSavingsTokens?: number;
  instructions?: string;
};

const DEFAULT_PRESERVED_TAIL_TOKENS = 12_000;
const DEFAULT_PRESERVED_TAIL_MESSAGES = 8;
const DEFAULT_MIN_TEXT_MESSAGES_TO_KEEP = 4;
const DEFAULT_ESTIMATED_SUMMARY_TOKENS = 2_000;

export function planCompactionBoundary(
  items: AgentInputItem[],
  options: CompactionPlannerOptions = {},
): CompactionPlan | null {
  const estimatedPreTokens = estimateTokensForItems(items, {
    instructions: options.instructions,
  });

  if (
    !options.force &&
    typeof options.thresholdTokens === 'number' &&
    estimatedPreTokens < options.thresholdTokens
  ) {
    return null;
  }

  const stableItems = buildStableHistoryItems(items);

  const compactableIndexes = stableItems
    .filter(({ item }) => !isSystemMessage(item))
    .map(({ index }) => index);
  if (compactableIndexes.length < 2) {
    return null;
  }

  const tailStartIndex = chooseTailStartIndex(items, options);
  const adjustedTailStartIndex = adjustTailStartForAssistantResponse(
    items,
    adjustTailStartForToolPairs(items, tailStartIndex),
  );

  const firstCompactableIndex = compactableIndexes[0]!;
  const compactedEndIndex = adjustedTailStartIndex - 1;
  if (compactedEndIndex < firstCompactableIndex) {
    return null;
  }

  const from = stableItems[firstCompactableIndex];
  const to = stableItems[compactedEndIndex];
  if (!from || !to) {
    return null;
  }

  const itemsForSummary = items
    .slice(firstCompactableIndex, compactedEndIndex + 1)
    .filter((item) => !isSystemMessage(item));

  const preservedItems = items.slice(adjustedTailStartIndex);
  if (itemsForSummary.length === 0 || preservedItems.length === 0) {
    return null;
  }

  const summaryTokens =
    options.estimatedSummaryTokens ?? DEFAULT_ESTIMATED_SUMMARY_TOKENS;
  const estimatedPostTokens =
    estimateTokensForItems(preservedItems, {
      instructions: options.instructions,
    }) +
    summaryTokens +
    estimateTokensForItems(items.filter(isSystemMessage));
  const savings = estimatedPreTokens - estimatedPostTokens;
  if (savings < (options.minSavingsTokens ?? 0)) {
    return null;
  }

  return {
    compactedRange: {
      fromItemId: from.id,
      toItemId: to.id,
    },
    preservedItems,
    itemsForSummary,
    insertAfterItemId: to.id,
    estimatedPreTokens,
    estimatedPostTokens,
  };
}

function chooseTailStartIndex(
  items: AgentInputItem[],
  options: CompactionPlannerOptions,
): number {
  const preservedTailTokens =
    options.preservedTailTokens ?? DEFAULT_PRESERVED_TAIL_TOKENS;
  const preservedTailMessages =
    options.preservedTailMessages ?? DEFAULT_PRESERVED_TAIL_MESSAGES;
  const minTextMessagesToKeep =
    options.minTextMessagesToKeep ?? DEFAULT_MIN_TEXT_MESSAGES_TO_KEEP;

  let tokens = 0;
  let messages = 0;
  let textMessages = 0;
  let tailStartIndex = items.length;

  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index]!;
    if (isSystemMessage(item)) {
      continue;
    }
    tailStartIndex = index;
    tokens += estimateTokensForItem(item);
    messages += 1;
    if (isTextMessage(item)) {
      textMessages += 1;
    }
    if (
      tokens >= preservedTailTokens &&
      messages >= preservedTailMessages &&
      textMessages >= minTextMessagesToKeep
    ) {
      break;
    }
  }

  if (tailStartIndex === items.length) {
    return Math.max(0, items.length - 1);
  }
  return tailStartIndex;
}

function adjustTailStartForToolPairs(
  items: AgentInputItem[],
  tailStartIndex: number,
): number {
  let adjusted = tailStartIndex;
  const callIdsInTail = new Set<string>();
  const resultCallIdsInTail = new Set<string>();

  for (let index = adjusted; index < items.length; index += 1) {
    const item = items[index];
    if (!item || typeof item !== 'object') {
      continue;
    }
    if (item.type === 'function_call' && typeof item.callId === 'string') {
      callIdsInTail.add(item.callId);
    } else if (
      item.type === 'function_call_result' &&
      typeof item.callId === 'string'
    ) {
      resultCallIdsInTail.add(item.callId);
    }
  }

  for (let index = adjusted - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (!item || typeof item !== 'object') {
      continue;
    }
    if (
      item.type === 'function_call' &&
      typeof item.callId === 'string' &&
      resultCallIdsInTail.has(item.callId)
    ) {
      adjusted = index;
    }
    if (
      item.type === 'function_call_result' &&
      typeof item.callId === 'string' &&
      callIdsInTail.has(item.callId)
    ) {
      adjusted = index;
    }
  }

  return adjusted;
}

function adjustTailStartForAssistantResponse(
  items: AgentInputItem[],
  tailStartIndex: number,
): number {
  let adjusted = tailStartIndex;

  while (adjusted > 0 && isAssistantOutputItem(items[adjusted - 1]!)) {
    adjusted -= 1;
  }

  return adjusted;
}

function isSystemMessage(item: AgentInputItem): boolean {
  return (
    item &&
    typeof item === 'object' &&
    item.type === 'message' &&
    item.role === 'system'
  );
}

function isTextMessage(item: AgentInputItem): boolean {
  return item && typeof item === 'object' && item.type === 'message';
}

function isAssistantOutputItem(item: AgentInputItem): boolean {
  if (!item || typeof item !== 'object') {
    return false;
  }
  if (item.type === 'reasoning' || item.type === 'function_call') {
    return true;
  }
  if ('role' in item) {
    return item.role === 'assistant';
  }
  if (item.type === 'unknown') {
    const role = item.providerData?.role;
    return role === 'assistant';
  }
  return false;
}
